/**
 * Keyed rendering for participant name chips.
 *
 * Re-rendering a list with innerHTML would replay the arrival animation of
 * every chip on every update. This updates chips in place by id: new names
 * pop in, names that leave shrink away, everyone else stays still and only
 * changes state (offline, renamed).
 */
import type { SessionParticipant } from '@schulspiele/protocol';

import { esc } from '../dom';

export interface ChipOptions {
  /**
   * The name is a button (`data-chip-menu`) that opens a small sheet with
   * "Umbenennen" and "Entfernen" (U19: two 16 px buttons side by side were
   * too small for a thumb).
   */
  controls?: boolean;
  /** The game is over: plain names, neither green nor grey. */
  ended?: boolean;
  /** Hide disconnected participants instead of showing them faded. */
  onlyConnected?: boolean;
  /**
   * Show disconnected participants faded (default). The board passes false:
   * a phone with a locked screen is still in the game, the class need not
   * see it (docs/STUDENT_DEVICES.md).
   */
  markOffline?: boolean;
  /** Shown when the list is empty. */
  emptyText?: string;
}

function chipInner(p: SessionParticipant, controls: boolean): string {
  return controls
    ? `<button type="button" class="name-chip__name" data-chip-menu="${esc(p.id)}" data-name="${esc(p.name)}" aria-label="${esc(p.name)}: umbenennen oder entfernen">${esc(p.name)}</button>`
    : `<span class="name-chip__name">${esc(p.name)}</span>`;
}

export function renderChips(
  container: HTMLElement,
  participants: SessionParticipant[],
  options: ChipOptions = {},
): void {
  const controls = options.controls === true;
  const list =
    options.onlyConnected === true ? participants.filter((p) => p.connected) : participants;
  const wanted = new Map(list.map((p) => [p.id, p]));

  container.querySelector('[data-empty]')?.remove();

  // Leave: shrink away, then remove.
  for (const el of container.querySelectorAll<HTMLElement>('.name-chip[data-pid]')) {
    const id = el.dataset.pid ?? '';
    if (!wanted.has(id) && !el.classList.contains('is-leaving')) {
      el.classList.add('is-leaving');
      el.addEventListener(
        'animationend',
        () => {
          el.remove();
        },
        { once: true },
      );
      // Fallback when animations are off.
      window.setTimeout(() => {
        el.remove();
      }, 600);
    }
  }

  // Update or add, keeping order.
  let previous: Element | null = null;
  for (const p of list) {
    let el = container.querySelector<HTMLElement>(`.name-chip[data-pid="${CSS.escape(p.id)}"]`);
    if (el === null) {
      el = document.createElement('span');
      el.className = 'name-chip is-new';
      el.dataset.pid = p.id;
      el.innerHTML = chipInner(p, controls);
      el.dataset.controls = String(controls);
      el.addEventListener('animationend', () => el?.classList.remove('is-new'), { once: true });
    } else {
      const name = el.querySelector('.name-chip__name');
      const renamed = name !== null && name.textContent !== p.name;
      // Controls come and go with the phase (after the end: plain names, U19).
      if (renamed || el.dataset.controls !== String(controls)) {
        el.innerHTML = chipInner(p, controls);
        el.dataset.controls = String(controls);
        if (renamed) restartAnimation(el, 'is-renamed');
      }
    }
    el.classList.toggle('name-chip--ended', options.ended === true);
    const offline = !p.connected && options.markOffline !== false && options.ended !== true;
    el.classList.toggle('name-chip--offline', offline);
    el.title = offline ? 'Verbindung unterbrochen' : '';
    if (previous === null) {
      if (container.firstElementChild !== el) container.prepend(el);
    } else if (previous.nextElementSibling !== el) {
      previous.after(el);
    }
    previous = el;
  }

  if (list.length === 0 && options.emptyText !== undefined) {
    container.insertAdjacentHTML(
      'beforeend',
      `<span class="muted" data-empty>${esc(options.emptyText)}</span>`,
    );
  }
}

/** A number that bumps when it changes ("Dabei: 12"). */
export function setCount(el: Element, text: string): void {
  if (el.textContent === text) return;
  el.textContent = text;
  restartAnimation(el, 'is-bumped');
}

/** Re-trigger a CSS animation class even if it is already set. */
function restartAnimation(el: Element, cls: string): void {
  el.classList.remove(cls);
  requestAnimationFrame(() => {
    el.classList.add(cls);
  });
}

/** PIN digits as separate spans so they can drop in one after another. */
export function pinDigits(pin: string): string {
  // PINs are ASCII digits, so splitting by code unit is safe here.
  return Array.from({ length: pin.length }, (_, i) => pin.charAt(i))
    .map(
      (d, i) =>
        `<span class="pin-digit" style="animation-delay:${String(80 + i * 70)}ms">${esc(d)}</span>`,
    )
    .join('');
}
